import { Component } from "@theme/component";
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from "@theme/utilities";
import { scrollIntoView } from "@theme/scrolling";
import { ZoomMediaSelectedEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @typedef {object} Loop
 * @property {'x' | 'y'} axis - The axis the strip scrolls on.
 * @property {number} setLength - How long a single, non repeated set is.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ["dialog", "media", "thumbnails"];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /**
   * Measurements of the looping thumbnails strip, or null while looping is inactive.
   * @type {Loop | null}
   */
  #thumbnailLoop = null;

  /**
   * Measurements of the looping zoomed gallery, or null while looping is inactive.
   * @type {Loop | null}
   */
  #galleryLoop = null;

  connectedCallback() {
    super.connectedCallback();

    const gallery = this.#getGallery();

    gallery?.addEventListener("scroll", this.handleScroll);
    gallery?.addEventListener("scroll", this.#handleGalleryScroll);
    this.refs.thumbnails?.addEventListener(
      "scroll",
      this.#handleThumbnailsScroll,
    );

    for (const strip of [gallery, this.refs.thumbnails]) {
      if (strip) this.#resizeObserver.observe(strip);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const gallery = this.#getGallery();

    gallery?.removeEventListener("scroll", this.handleScroll);
    gallery?.removeEventListener("scroll", this.#handleGalleryScroll);
    this.refs.thumbnails?.removeEventListener(
      "scroll",
      this.#handleThumbnailsScroll,
    );
    this.#resizeObserver.disconnect();
  }

  /** @returns {HTMLElement | null} */
  #getGallery() {
    return this.querySelector(".dialog-zoomed-gallery");
  }

  /**
   * Every slide of the zoomed gallery, loop clones included, in DOM order.
   * @returns {HTMLElement[]}
   */
  #getGalleryItems() {
    return Array.from(this.#getGallery()?.querySelectorAll(":scope > li") ?? []);
  }

  /**
   * Reads the media index an element stands for, whatever copy of it it is.
   * @param {Element | null | undefined} element - The slide or thumbnail to read.
   * @returns {number} The index, or -1 when the element carries none.
   */
  #getMediaIndex(element) {
    const index = Number(element?.getAttribute("data-index"));

    return Number.isInteger(index) ? index : -1;
  }

  /**
   * Every thumbnail button, loop clones included, in DOM order.
   * @returns {HTMLElement[]}
   */
  #getThumbnails() {
    return Array.from(this.refs.thumbnails?.querySelectorAll("button") ?? []);
  }

  /**
   * Only the thumbnails rendered by Liquid, i.e. one button per media.
   * @returns {HTMLElement[]}
   */
  #getOriginalThumbnails() {
    return this.#getThumbnails().filter(
      (thumbnail) => !thumbnail.hasAttribute("data-loop-clone"),
    );
  }

  #getCurrentIndex() {
    const selected = this.refs.thumbnails?.querySelector(
      'button[aria-selected="true"]',
    );

    return Math.max(this.#getMediaIndex(selected), 0);
  }

  prevSlide() {
    this.#goToSlide(this.#getCurrentIndex() - 1);
  }

  nextSlide() {
    this.#goToSlide(this.#getCurrentIndex() + 1);
  }

  /**
   * Moves to a slide, looping around when the index goes past either end.
   * @param {number} index - The (possibly out of range) index to move to.
   */
  #goToSlide(index) {
    const total = this.refs.media.length;
    if (total === 0) return;

    // Wrap around so the last image goes back to the first one and vice versa.
    const target = ((index % total) + total) % total;
    const wrapped = target !== index;
    const behavior = prefersReducedMotion() ? "instant" : "smooth";

    // On a looping strip a wrap is just a step into the next copy, so it can
    // animate. Without one it would scroll back across every single slide.
    this.selectThumbnail(target, {
      behavior: wrapped && !this.#thumbnailLoop ? "instant" : behavior,
      mediaBehavior: wrapped && !this.#galleryLoop ? "instant" : behavior,
    });
  }

  /**
   * Repeats a set of elements once before and once after itself, so the strip
   * reads as endless, and measures how long a single set is.
   *
   * Idempotent: once the copies exist it only re-measures them.
   *
   * @param {HTMLElement} container - The scrolling strip.
   * @param {HTMLElement[]} originals - The elements rendered by Liquid.
   * @param {'x' | 'y'} axis - The axis the strip scrolls on.
   * @returns {Loop | null} The measurements, or null if the strip cannot loop.
   */
  #buildLoop(container, originals, axis) {
    // data-loop="false" lets a gallery (e.g. the image grid section) end at its last image
    if (originals.length < 2 || this.dataset.loop === "false") return null;

    if (!container.querySelector("[data-loop-clone]")) {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      for (const element of originals) {
        before.append(createLoopClone(element));
        after.append(createLoopClone(element));
      }

      container.prepend(before);
      container.append(after);
    }

    // The gap between a copy and its original spans one whole set, gaps included.
    const first = container.firstElementChild;
    if (!first) return null;

    const firstRect = first.getBoundingClientRect();
    const originalRect = originals[0].getBoundingClientRect();
    const setLength =
      axis === "y"
        ? originalRect.top - firstRect.top
        : originalRect.left - firstRect.left;

    return setLength > 0 ? { axis, setLength } : null;
  }

  /**
   * Shifts a looping strip by whole sets until its scroll position sits back in
   * the middle of the copies. Since the content repeats, the shift is invisible.
   *
   * @param {HTMLElement | null} container - The scrolling strip.
   * @param {Loop | null} loop - Its measurements.
   */
  #recenter(container, loop) {
    if (!container || !loop) return;

    const { axis, setLength } = loop;
    const current = axis === "y" ? container.scrollTop : container.scrollLeft;
    const max =
      axis === "y"
        ? container.scrollHeight - container.clientHeight
        : container.scrollWidth - container.clientWidth;

    // Leave an equal amount of copied runway on both sides of the allowed band.
    const lower = (max - setLength) / 2;
    let next = current;

    while (next < lower) next += setLength;
    while (next >= lower + setLength) next -= setLength;

    if (Math.abs(next - current) < 1) return;

    container.scrollTo({
      [axis === "y" ? "top" : "left"]: next,
      behavior: "instant",
    });
  }

  /**
   * Finds the copy of an index sitting closest to what the strip currently shows,
   * so moving to it never scrolls further than a single step.
   *
   * @param {HTMLElement | null} container - The scrolling strip.
   * @param {HTMLElement[]} candidates - Every element of the strip, copies included.
   * @param {number} index - The media index to look for.
   * @param {'x' | 'y'} axis - The axis the strip scrolls on.
   * @returns {HTMLElement | null}
   */
  #getClosestCopy(container, candidates, index, axis) {
    const matches = candidates.filter(
      (element) => this.#getMediaIndex(element) === index,
    );

    if (!container || matches.length < 2) return matches[0] ?? null;

    const containerRect = container.getBoundingClientRect();
    const center =
      axis === "y"
        ? containerRect.top + containerRect.height / 2
        : containerRect.left + containerRect.width / 2;

    /** @param {HTMLElement} element */
    const distance = (element) => {
      const rect = element.getBoundingClientRect();

      return Math.abs(
        (axis === "y" ? rect.top + rect.height / 2 : rect.left + rect.width / 2) -
          center,
      );
    };

    return matches.reduce((closest, match) =>
      distance(match) < distance(closest) ? match : closest,
    );
  }

  /**
   * Sets up the endless thumbnails strip. Runs on open and on resize.
   */
  #setupThumbnailLoop() {
    const thumbnails = this.refs.thumbnails;
    if (!thumbnails) return;

    const vertical = thumbnails.scrollHeight > thumbnails.clientHeight;
    const horizontal = thumbnails.scrollWidth > thumbnails.clientWidth;

    // While the list is hidden, or short enough that every thumbnail already
    // fits, there is nothing to loop through.
    if (!vertical && !horizontal) return;

    this.#thumbnailLoop = this.#buildLoop(
      thumbnails,
      this.#getOriginalThumbnails(),
      vertical ? "y" : "x",
    );
    this.#recenter(thumbnails, this.#thumbnailLoop);
  }

  /**
   * Sets up the endless zoomed gallery, so the first image slides in right after
   * the last one instead of the view rewinding across the whole set.
   */
  #setupGalleryLoop() {
    const gallery = this.#getGallery();
    const { media } = this.refs;

    if (!gallery || gallery.scrollWidth <= gallery.clientWidth) return;

    // Videos and 3D models cannot be duplicated without spawning a second player,
    // so the endless strip is limited to galleries made purely of images.
    const imagesOnly = media.every((item) =>
      item.classList.contains("product-media-container--image"),
    );

    if (!imagesOnly) return;

    this.#galleryLoop = this.#buildLoop(gallery, media, "x");
    this.#recenter(gallery, this.#galleryLoop);
  }

  /**
   * Re-measures both strips, since a slide is sized relative to the viewport and
   * the thumbnails switch axis at the desktop breakpoint.
   */
  #handleResize = () => {
    this.#setupThumbnailLoop();
    this.#setupGalleryLoop();
  };

  #handleThumbnailsScroll = debounce(
    () => this.#recenter(this.refs.thumbnails, this.#thumbnailLoop),
    100,
  );

  #handleGalleryScroll = debounce(
    () => this.#recenter(this.#getGallery(), this.#galleryLoop),
    100,
  );

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Finds the copy of a thumbnail closest to what the strip currently shows.
   * @param {number} index - The media index to look for.
   * @returns {HTMLElement | null}
   */
  #getClosestThumbnail(index) {
    return this.#getClosestCopy(
      this.refs.thumbnails,
      this.#getThumbnails(),
      index,
      this.#thumbnailLoop?.axis ?? "x",
    );
  }

  /**
   * Finds the copy of a slide closest to what the gallery currently shows.
   * @param {number} index - The media index to look for.
   * @returns {HTMLElement | null}
   */
  #getClosestGalleryItem(index) {
    return this.#getClosestCopy(
      this.#getGallery(),
      this.#getGalleryItems(),
      index,
      "x",
    );
  }

  /**
   * Marks a media index as selected across every copy of its thumbnail.
   * @param {number} index - The media index to mark as selected.
   */
  #setActiveThumbnail(index) {
    for (const thumbnail of this.#getThumbnails()) {
      thumbnail.setAttribute(
        "aria-selected",
        `${this.#getMediaIndex(thumbnail) === index}`,
      );
    }
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
      dialog.showModal();

      // The strips can only be measured once the dialog is laid out.
      this.#setupThumbnailLoop();
      this.#setupGalleryLoop();
      this.#setActiveThumbnail(index);

      for (const target of [this.#getClosestThumbnail(index), targetImage]) {
        target?.scrollIntoView({ behavior: "instant" });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event.target instanceof Element
        ? event.target.closest("li,slideshow-slide")
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage ||
      !targetImage
    )
      return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty("view-transition-name", transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty("view-transition-name");
      targetImage.style.setProperty("view-transition-name", transitionName);
    });

    targetImage.style.removeProperty("view-transition-name");

    this.selectThumbnail(index, { behavior: "instant" });
  }

  /**
   * Loads a high-resolution image for a specific media container.
   *
   * The upgrade is applied to every loop copy of that media at once, so the strip
   * never shows the same image at two different resolutions.
   *
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains("product-media-container--image"))
      return false;

    const image = mediaContainer.querySelector("img.product-media__image");
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute("data_max_resolution");
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl))
      return false;

    preloadImage(highResolutionUrl);

    const index = this.#getMediaIndex(mediaContainer);
    const containers =
      index === -1
        ? [mediaContainer]
        : this.#getGalleryItems().filter(
            (item) => this.#getMediaIndex(item) === index,
          );

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute("data_max_resolution", highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      for (const container of containers) {
        const target = container.querySelector("img.product-media__image");
        if (!(target instanceof HTMLImageElement)) continue;

        target.replaceWith(newImage.cloneNode(true));
      }

      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const mostVisibleElement = await getMostVisibleElement(
      this.#getGalleryItems(),
    );
    const activeIndex = this.#getMediaIndex(mostVisibleElement);

    if (activeIndex === -1) return;

    this.#setActiveThumbnail(activeIndex);
    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice())
      return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(
      this.#getGalleryItems(),
    );

    // Get the index and set up transition
    const activeIndex = this.#getMediaIndex(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery =
      /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest("media-gallery")
      );

    const slideshowActive = mediaGallery?.presentation === "carousel";

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add("dialog--closed");

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty(
      "view-transition-name",
      transitionName,
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty("view-transition-name");
      slide.style.setProperty("view-transition-name", transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty("view-transition-name");
    dialog.classList.remove("dialog--closed");
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? "instant" : "smooth";
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} [options.behavior] - The behavior of the thumbnails scroll.
   * @param {ScrollBehavior} [options.mediaBehavior] - The behavior of the zoomed gallery scroll.
   */
  async selectThumbnail(index, options = {}) {
    const { media, thumbnails } = this.refs;
    const { behavior = "smooth", mediaBehavior = behavior } = options;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= media.length) return;

    this.#setActiveThumbnail(index);

    // Recenter both strips first, so neither animation runs out of copies midway.
    this.#recenter(thumbnails, this.#thumbnailLoop);
    this.#recenter(this.#getGallery(), this.#galleryLoop);

    const targetThumbnail = this.#getClosestThumbnail(index);

    if (targetThumbnail && thumbnails) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior,
        block: "center",
        inline: "center",
      });
    }

    const targetImage = this.#getClosestGalleryItem(index) ?? media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: mediaBehavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get("zoom-dialog")) {
  customElements.define("zoom-dialog", ZoomDialog);
}

/**
 * Creates a repeated copy of a strip element. The `on:` attributes are delegated
 * from the document, so the copy keeps working without any extra wiring, but `id`
 * and `ref` are dropped: ids have to stay unique and refs would make the component
 * treat the copy as another media.
 *
 * @param {HTMLElement} element - The element to copy.
 * @returns {HTMLElement} The copy, hidden from assistive technology.
 */
function createLoopClone(element) {
  const clone = /** @type {HTMLElement} */ (element.cloneNode(true));

  for (const node of [clone, ...clone.querySelectorAll("[id], [ref]")]) {
    node.removeAttribute("id");
    node.removeAttribute("ref");
  }

  clone.setAttribute("data-loop-clone", "");
  clone.setAttribute("aria-hidden", "true");
  clone.setAttribute("tabindex", "-1");

  return clone;
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev,
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      },
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
